// Schedule Demo Page - Stages 07_API_CONNECTED and 10_DEMO_SCHEDULED
// Personalised demo booking after dashcam provider API connected
// UPDATED: Added polling for backend sync and blur/focus handling for reschedule/cancel
// UPDATED: Added demo mode support
import { useEffect, useRef, useMemo } from 'react'
import { InlineWidget } from 'react-calendly'
import { useOnboarding } from '../../context/OnboardingContext'
import { useAuth } from '../../context/AuthContext'
import { useDemoMode } from '../../hooks/useDemoMode'
import { n8nApi } from '../../services/n8nApi'
import { CALENDLY_CONFIG, getUtmCampaign } from '../../config/calendly'

const ScheduleDemoPage = () => {
  const { prospectData, effectiveUid } = useOnboarding()
  const { user } = useAuth()
  const { isDemoMode, demoStage, exitDemo } = useDemoMode()

  // Track whether we're awaiting return from external Calendly page
  const isAwaitingExternalAction = useRef(false)

  // In demo mode, show "booked" state for stage 10
  const hasDemoScheduled = useMemo(() => {
    if (isDemoMode && demoStage === '10_DEMO_SCHEDULED') {
      return true
    }
    return prospectData.meetingScheduled && prospectData.meetingScheduledAt
  }, [isDemoMode, demoStage, prospectData.meetingScheduled, prospectData.meetingScheduledAt])

  // Listen for Calendly events AND window blur/focus (for reschedule/cancel)
  useEffect(() => {
    // DEMO MODE: Skip all event listeners and polling
    if (isDemoMode) return

    // Store original values to detect changes
    const originalStatus = prospectData.meetingScheduled
    const originalTime = prospectData.meetingScheduledAt

    // Poll until data changes or max attempts reached
    const pollUntilSynced = async () => {
      const maxAttempts = 10
      const pollInterval = 1000
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval))
        
        try {
          const response = await n8nApi.getUserStatus(effectiveUid)
          
          if (response.found && response.prospect) {
            const newStatus = response.prospect.meeting_scheduled
            const newTime = response.prospect.meeting_scheduled_at
            
            // Check if anything changed
            if (newStatus !== originalStatus || newTime !== originalTime) {
              console.log(`✅ Change detected after ${attempt} attempts`)
              window.location.reload()
              return
            }
          }
          
          console.log(`⏳ Attempt ${attempt}/${maxAttempts} - no change yet`)
        } catch (err) {
          console.error('Poll error:', err)
        }
      }
      
      console.log('⚠️ Max attempts reached, no change detected')
    }

    // Handle embedded widget events (new bookings)
    const handleCalendlyEvent = async (e) => {
      if (e.origin !== 'https://calendly.com') return
      
      if (e.data.event === 'calendly.event_scheduled') {
        console.log('✅ Demo meeting booked! Waiting for backend sync...')
        await pollUntilSynced()
      }
    }

    // Handle user leaving the page (clicking reschedule/cancel links)
    const handleWindowBlur = () => {
      if (hasDemoScheduled) {
        // User clicked away while meeting was scheduled (likely reschedule/cancel link)
        isAwaitingExternalAction.current = true
        console.log('👋 Window lost focus, will check for updates on return')
      }
    }

    // Handle returning from external Calendly pages (reschedule/cancel)
    const handleWindowFocus = async () => {
      if (!isAwaitingExternalAction.current) return
      
      console.log('🔄 Window refocused after external action, checking for updates...')
      isAwaitingExternalAction.current = false
      await pollUntilSynced()
    }

    window.addEventListener('message', handleCalendlyEvent)
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)
    
    return () => {
      window.removeEventListener('message', handleCalendlyEvent)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [effectiveUid, hasDemoScheduled, prospectData.meetingScheduled, prospectData.meetingScheduledAt, isDemoMode])

  const formatMeetingTime = (isoString) => {
    if (!isoString) return null
    const date = new Date(isoString)
    return {
      date: date.toLocaleDateString(undefined, { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      }),
      time: date.toLocaleTimeString(undefined, { 
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      })
    }
  }

  // In demo mode, generate mock meeting time (day after tomorrow at 10am)
  const meetingTime = useMemo(() => {
    if (!hasDemoScheduled) return null

    if (isDemoMode) {
      const dayAfterTomorrow = new Date()
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2)
      dayAfterTomorrow.setHours(10, 0, 0, 0)
      return formatMeetingTime(dayAfterTomorrow.toISOString())
    }

    return formatMeetingTime(prospectData.meetingScheduledAt)
  }, [hasDemoScheduled, isDemoMode, prospectData.meetingScheduledAt])

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-strong">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-950">
          {hasDemoScheduled 
            ? 'Your Personalised Demo is Booked!'
            : 'Your Integration is Complete!'
          }
        </h1>
        <p className="mt-3 text-slate-600">
          {hasDemoScheduled 
            ? 'Robert will walk you through the risk patterns we identified in your fleet data.'
            : 'Schedule your personalised demo walkthrough with Robert Putt to see the risk analysis from your fleet.'
          }
        </p>
      </div>

      {/* What we found - shown before booking */}
      {!hasDemoScheduled && (
        <div className="space-y-4 mb-8">
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <span className="text-accent-700 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">Review your risk analysis</h3>
                <p className="text-slate-600 mt-1">
                  Robert will walk through the specific risk patterns and claim indicators 
                  we identified in your dashcam footage.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <span className="text-accent-700 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">Discuss risk reduction strategies</h3>
                <p className="text-slate-600 mt-1">
                  We'll identify which drivers and routes present the highest nuclear claim risk 
                  and discuss intervention strategies.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <span className="text-accent-700 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">Get your implementation plan</h3>
                <p className="text-slate-600 mt-1">
                  If FleetVision is a fit, we'll create a tailored rollout plan for{' '}
                  {prospectData.companyName || 'your organization'}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting confirmation or booking widget */}
      {hasDemoScheduled && meetingTime ? (
        <div className="card mb-8 bg-accent-50 border-accent-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-accent-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-accent-900 mb-1">
                Personalised Demo Confirmed
              </h2>
              <p className="text-sm text-accent-700">
                We'll walk through your fleet's risk analysis together.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 border border-accent-200">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="font-semibold text-brand-950 text-lg">{meetingTime.date}</p>
                <p className="text-slate-600">{meetingTime.time}</p>
              </div>
            </div>

            {/* Hide reschedule/cancel in demo mode */}
            {!isDemoMode && (
              <div className="flex flex-col sm:flex-row gap-3">
                {prospectData.calendlyRescheduleUrl && (
                  <a
                    href={prospectData.calendlyRescheduleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary flex-1 justify-center"
                  >
                    Reschedule
                  </a>
                )}
                {prospectData.calendlyCancelUrl && (
                  <a
                    href={prospectData.calendlyCancelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex-1 justify-center text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    Cancel
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-brand-950">Schedule Your Personalised Demo</h2>
              <p className="text-sm text-slate-600">45 minutes with Robert</p>
            </div>
          </div>
          
          <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
            <InlineWidget
              url={CALENDLY_CONFIG.demo.eventUrl}
              prefill={{
                name: prospectData.fullName || '',
                email: prospectData.email || user?.email || '',
                customAnswers: { a1: prospectData.companyName || '' }
              }}
              utm={{
                utmSource: CALENDLY_CONFIG.demo.utmSource,
                utmMedium: CALENDLY_CONFIG.demo.utmMedium,
                utmCampaign: getUtmCampaign(effectiveUid)
              }}
              styles={CALENDLY_CONFIG.styles}
            />
          </div>
        </div>
      )}

      {/* Contact options */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h2 className="font-semibold text-brand-950 mb-4">Questions before your demo?</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <a href="mailto:sales@insurevision.ai" className="btn-secondary flex-1 justify-center">
            Email Us
          </a>
          <a href="tel:+447976454426" className="btn-primary flex-1 justify-center">
            Call Us
          </a>
        </div>
      </div>
    </div>
  )
}

export default ScheduleDemoPage
